import React from 'react';
import { Button } from './Button';
import { AlertCircle } from 'lucide-react';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  issues?: string[];
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
}

export function ConfirmDialog({
  isOpen,
  title,
  message,
  issues = [],
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
}: ConfirmDialogProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#221F1F] border border-[#504A4A] rounded-lg w-full max-w-md shadow-lg">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#504A4A] flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
          <h2 className="text-lg font-semibold text-[#EBFFEE]">{title}</h2>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <p className="text-[#EBFFEE] mb-4">{message}</p>
          
          {issues.length > 0 && (
            <div className="mb-4 p-3 bg-yellow-900/30 border border-yellow-600 rounded-md">
              <p className="text-yellow-200 text-sm font-medium mb-2">Please fix the following issues:</p>
              <ul className="text-yellow-200 text-sm list-disc list-inside space-y-1">
                {issues.map((issue, index) => (
                  <li key={index}>{issue}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-end mt-6">
            <Button
              onClick={onCancel}
              className="bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] border border-[#504A4A]"
              data-testid="confirm-dialog-cancel-button"
            >
              {cancelText}
            </Button>
            <Button
              onClick={onConfirm}
              className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]"
              data-testid="confirm-dialog-confirm-button"
            >
              {confirmText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

